import React, { useCallback, useState, useEffect } from 'react';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import AddSessionItemButton from './NewItemButton.component';
import { DetailsProps } from './DetailsProps.types';
import FileDragAndDrop from '../FileDrag&Drop/FileDragAndDrop.component';
import { Prompt, SubText } from '../Prompt/Prompt.component';
import {
  extractFileType,
  FileType,
  getExtFromFileName,
  getFileTypeFromExtension,
  MAX_FILE_SIZE,
} from '../../utils/fileProcessing';
import { IGradeRulesDetails, IIdResult, Scheme, Task } from '../../models/session.types';
import { WorkMode, NewTreeItemId } from '../SessionTree/SessionTree.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { useLayoutContext } from '../../Context/Layout.context';
import EnvWindow from '../../utils/env';
import {
  createExamGradeRule,
  DeleteExamGradeRuleAttachment,
  getExamGradeRule,
  modifyExamGradeRule,
} from '../../services/exams/examsGradeRule/examsGradeRule.service';
import { AxiosResponse } from 'axios';
import { useStyles } from './GradeRulesDetails.styles';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import TooltipOneComponent from '../TooltipOne/TooltipOne.component';
import { Grid, SvgIcon, Typography } from '@material-ui/core';
import SwitchOne from '../SwitchOne/SwitchOne.component';
import DeleteButton from './DeleteButton.component';

interface TaskDetailsProps extends DetailsProps {
  data: IGradeRulesDetails;
  task: Task | null;
  isReadOnly: () => boolean;
}

const GradeRulesDetails: React.FC<TaskDetailsProps> = (props) => {
  const classes = useStyles();
  const sessionDetailsClasses = sessionDetails();

  const [isAddedFile, setIsAddedFile] = useState<boolean>(false);
  const [isScorin, setIsScoring] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [, setIsEdited] = useState<boolean>(false);
  const isThereAnyFile = props.data.content !== null && props.data.content !== undefined && props.data.content !== '';
  const [isAutomaticScoringChecked, setIsAutomaticScoringChecked] = useState<boolean>(
    props.data.automaticScoring ?? false,
  );
  const [isNoAnswerChecked, setNoAnswerChecked] = useState<boolean>(props.data.noAnswer ?? false);
  const [rulesFile, setRulesFile] = useState<File | undefined>();
  const link = `${EnvWindow.REACT_APP_EXAMS__EXAM_GRADE_RULE_API_URL}/${props.data.id}/document`;
  const [isProblemWithFile, setIsProblemWithFile] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorSubTexts, setErrorSubTexts] = useState<SubText[]>();
  const [noAnswerValidationError, setNoAnswerValidationError] = useState<boolean>(false);
  const [enchacedError, setEnchacedError] = useState<JSX.Element>(<div></div>);
  const { openAlert } = useLayoutContext();

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  useEffect(() => {
    if (isThereAnyFile && props.data.id !== NewTreeItemId.GradeRules) {
      getExamGradeRule(props.data.id.toString())
        .then((response) => {
          const fileExt = getExtFromFileName(props.data.content ?? '');
          const image = new File([response.data], props.data.content ?? '', {
            type: getFileTypeFromExtension(fileExt) ?? 'image/png',
          });
          setRulesFile(image);
          setIsAddedFile(true);
        })
        .catch((e) => {
          console.log(e);
          openAlert('error');
        });
    }
  }, [isThereAnyFile, props.data.id]);

  const fileIsIdentical = props.data.content ? rulesFile?.name === props.data.content : true;

  const setDisabled = useCallback(() => {
    if (props.workMode === WorkMode.ADD) {
      setIsDisabled(false);
    } else if (
      isAutomaticScoringChecked === props.data.automaticScoring &&
      isNoAnswerChecked === props.data.noAnswer &&
      fileIsIdentical
    ) {
      setIsDisabled(true);
    } else if (props.data.automaticScoring === undefined) {
      setIsDisabled(false);
    } else {
      setIsDisabled(false);
    }
  }, [isAutomaticScoringChecked, isNoAnswerChecked, rulesFile?.name, props.data.automaticScoring, props.data.noAnswer]);

  useEffect(() => {
    setDisabled();
  }, [setDisabled]);

  const acceptedFileExt = ['jpg', 'jpeg', 'gif', 'png'];
  const acceptedFileTypes: FileType[] = ['image/jpeg', 'image/gif', 'image/png'];

  const handleFilesUploaded = useCallback(
    (files: FileList) => {
      files[0].arrayBuffer().then((buffer) => {
        if (files[0].size <= MAX_FILE_SIZE) {
          const fileExt = getExtFromFileName(files[0].name);
          const extractedType = extractFileType(buffer);
          if (
            files[0].type === extractedType &&
            acceptedFileTypes.includes(extractedType) &&
            acceptedFileExt.includes(fileExt)
          ) {
            setIsProblemWithFile(false);
            setRulesFile(files[0]);
            setIsDisabled(false);
          } else {
            setErrorTitle('Nieprawidłowy format pliku');
            setErrorSubTexts([{ text: 'Akceptowany format załączników to: ' }, { text: acceptedFileExt.join(', ') }]);
            setIsProblemWithFile(true);
            setIsDisabled(true);
            console.error(`Format pliku deklarowany: ${files[0].type} odczytany: ${extractedType}`);
          }
        } else {
          setErrorTitle('Wybrany plik jest za duży');
          setErrorSubTexts([
            { text: 'Maksymalny rozmiar załączników to: ' },
            { text: `${MAX_FILE_SIZE / 1024 / 1024}MB` },
          ]);
          setIsProblemWithFile(true);
          setIsDisabled(true);
        }
      });
    },
    [rulesFile],
  );

  const handleRemoveFile = useCallback(() => {
    setRulesFile(undefined);
    setIsEdited(true);
  }, [rulesFile]);

  const areSchemasValidWithNoAnswer = () => {
    if (!isNoAnswerChecked) return true;

    const schemas = props.task?.gradeRules?.schemas ?? [];
    const missingZeroScoreSchemas: Scheme[] = [];
    for (let i = 0; i < schemas.length; i++) {
      const schema = schemas[i];
      if (schema.scores && schema.scores.length > 0) {
        const zeroScore = schema.scores.find((x) => x.value === 0);
        if (!zeroScore) {
          missingZeroScoreSchemas.push(schema);
        }
      } else {
        setNoAnswerValidationError(true);
        return false;
      }
    }
    if (missingZeroScoreSchemas.length > 0) {
      setEnchacedError(
        <>
          <Typography variant="body1" className={classes.validationErrorText}>
            Nie możesz zapisać zmian. Dla kryterium
          </Typography>
          {missingZeroScoreSchemas.map((schema, index) => (
            <TooltipOneComponent title={`${schema.name}`} key={`tooltip-error-${index}`} placement="top">
              <Typography variant="body1" className={classes.validationErrorTextBold} key={`typography-error-${index}`}>
                {`${index === 0 ? ' ' : ''}${schema.label}${index + 1 !== missingZeroScoreSchemas.length ? ', ' : ' '}`}
              </Typography>
            </TooltipOneComponent>
          ))}
          <Typography variant="body1" className={classes.validationErrorText}>
            nie wprowadzono wartości &apos;0&apos; w schemacie punktowania.
          </Typography>
        </>,
      );
      setNoAnswerValidationError(true);

      return false;
    }
    return true;
  };

  const saveChanges = async () => {
    if (props.data.id !== NewTreeItemId.GradeRules) {
      const isValid = areSchemasValidWithNoAnswer();
      if (!isValid) {
        return;
      }
      await modifyExamGradeRule(props.data.id, isNoAnswerChecked, isAutomaticScoringChecked, rulesFile).then(
        async () => {
          if (!!rulesFile == false && isAddedFile) {
            await DeleteExamGradeRuleAttachment(props.data.id).then(
              () => {
                openAlert('success');
                props.setWorkMode(WorkMode.VIEW);
                setDisabled();
                props.onSave({ id: props.data.id }, false);
                setIsAddedFile(false);
              },
              () => {
                openAlert('error');
              },
            );
          } else {
            openAlert('success');
            props.setWorkMode(WorkMode.VIEW);
            setDisabled();
            props.onSave({ id: props.data.id }, false);
          }
        },
        () => {
          openAlert('error');
        },
      );
    } else {
      const isValid = areSchemasValidWithNoAnswer();
      if (!isValid) {
        return;
      }

      await createExamGradeRule(props.data.parentId, isNoAnswerChecked, isAutomaticScoringChecked, rulesFile).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, true);
        },
        () => {
          openAlert('error');
        },
      );
    }
  };

  const revert = () => {
    setIsAutomaticScoringChecked(props.data.automaticScoring);
    setNoAnswerChecked(props.data.noAnswer);
    if (!props.data.content) setRulesFile(undefined);
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.TASK, revert);
  };

  const ShowConfirmationScoring = (param: boolean) => {
    if (param) {
      setIsScoring(true);
      setIsAutomaticScoringChecked(false);
      setIsEdited(true);
    } else {
      setIsAutomaticScoringChecked(true);
    }
  };

  const onConfirmScoring = () => {
    setIsAutomaticScoringChecked(false);
    setIsScoring(false);
  };

  const editingDisabled = props.workMode === WorkMode.VIEW;

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton workMode={props.workMode} isDisabled={props.isReadOnly()} handleEditMode={handleEditMode} />
            ) : (
              <DeleteButton
                isDisabled={props.isReadOnly()}
                workMode={props.workMode}
                // handleDeleteMode={props.onDelete}  to do: missing action of grade rules delete
              />
            )}
          </Grid>
          <Grid container justifyContent="center" alignContent="center">
            <Grid container justifyContent="center" className={sessionDetailsClasses.headerRow}>
              {props.workMode === WorkMode.VIEW && (
                <div className={sessionDetailsClasses.headerText}>Zasady oceniania</div>
              )}
              {props.workMode === WorkMode.EDIT && (
                <div className={sessionDetailsClasses.headerText}>Edycja zasad oceniania</div>
              )}
              {props.workMode === WorkMode.ADD && (
                <div className={sessionDetailsClasses.headerText}>Zasady oceniania</div>
              )}
            </Grid>

            <Grid container justifyContent="center" alignContent="center" direction="column">
              <Grid container justifyContent="space-between" alignItems="center" className={classes.solutionRules}>
                Brak odpowiedzi
                <TooltipOneComponent
                  title={
                    isNoAnswerChecked
                      ? 'W tym zadaniu opcja „Brak odpowiedzi” jest dostępna dla egzaminatora'
                      : 'W tym zadaniu opcja „Brak odpowiedzi” nie jest dostępna dla egzaminatora'
                  }
                  placement="top"
                >
                  <span>
                    <SwitchOne
                      disableRipple
                      checked={isNoAnswerChecked ? isNoAnswerChecked : false}
                      disabled={editingDisabled}
                      color={isNoAnswerChecked ? 'primary' : 'default'}
                      inputProps={{ 'aria-label': 'brak odpowiedzi' }}
                      onChange={() => {
                        setNoAnswerChecked(!isNoAnswerChecked);
                        setIsEdited(true);
                      }}
                    />
                  </span>
                </TooltipOneComponent>
              </Grid>
              <Grid container justifyContent="flex-start" className={classes.filesDragRow}>
                <FileDragAndDrop
                  handleFilesUpload={handleFilesUploaded}
                  acceptedExtension={acceptedFileExt}
                  handleRemoveFile={handleRemoveFile}
                  ruleFile={rulesFile}
                  fileSize={rulesFile?.size}
                  fileName={rulesFile?.name}
                  isEditMode={props.workMode !== WorkMode.VIEW}
                  id={props.data.id !== NewTreeItemId.GradeRules ? props.data.id : props.data.parentId}
                  link={link}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end">
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj schemat punktowania"
                disabled={props.isSessionImported || props.data.isExamSheetConfigured}
                onClick={() => props.onAddChild(BreadcrumbLevel.SCHEMA, props.data.id)}
              />
            )}

            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons disable={isDisabled} saveHandle={saveChanges} closeHandle={closeHandle} />
            )}
          </Grid>
        </Grid>
      )}
      {isProblemWithFile && (
        <Prompt
          type={'error'}
          open={isProblemWithFile}
          mainText={errorTitle}
          subTexts={errorSubTexts}
          onConfirm={() => setIsProblemWithFile(false)}
        />
      )}
      {noAnswerValidationError && (
        <Prompt
          type={'error'}
          open={noAnswerValidationError}
          mainText={errorTitle}
          enhancedText={enchacedError}
          onConfirm={() => setNoAnswerValidationError(false)}
        />
      )}
      {}
      <Prompt
        type="confirmation"
        open={isScorin}
        mainText={`Czy na pewno chcesz wyłączyć`}
        subTexts={[
          {
            text: 'punktację automatyczną ?',
            fontSize: 20,
          },
        ]}
        mainActionButtonText="TAK"
        onConfirm={() => {
          onConfirmScoring();
        }}
        onCancel={() => {
          setIsScoring(false);
          setIsAutomaticScoringChecked(true);
        }}
      />
    </>
  );
};

export default GradeRulesDetails;
